import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/useAuth';
import { fetchIssues, createSubscription, updateIssue, fetchSpotProducts } from '../services/dataService';
import { PackagePlus, Search, ExternalLink, Save, Filter, Copy, Check, PackageSearch } from 'lucide-react';
import { PageHeader } from '../components/layout';
import { Button, Input, Select, Card, Badge, Spinner, EmptyState, Modal } from '../components/ui';
import { cn } from '../utils/cn';

const STATUS_OPTIONS = ['Pending', 'Reviewed', 'Created'];
const CREATE_PRODUCTS_URL = 'https://www.abconsultingpr.com/sw1dbtables/products/';

// Named window target for spot links. A named target (rather than _blank)
// makes every spot open in the same reused tab, so working down the list
// updates one tab instead of piling up a new one per click.
//
// These links deliberately omit rel="noopener noreferrer", which every other
// external link here sets. Both keywords force the browser to create a fresh
// top-level context and ignore the target name (noreferrer implies noopener),
// which is exactly the tab-per-click behavior we're removing. The tradeoff is
// that the spot tab gets a window.opener handle back to this page; acceptable
// only because spot links point at our own SW2 host. Reference links, which
// point at arbitrary vendor sites, keep _blank + noopener.
const SPOT_WINDOW_NAME = 'abc-spot-viewer';

// Status filter. Single-select: exactly one status is active at a time, so
// picking one replaces the current selection rather than adding to it. The
// count badges in the page header double as the filter control, so each entry
// carries the Badge variant it renders with. '' is the bucket for rows with no
// resolution status set yet, surfaced to users as "New Requests".
// Order here drives the header order.
const STATUS_FILTER_OPTIONS = [
  { key: 'Created', label: 'Created', variant: 'success' },
  { key: 'Pending', label: 'Pending', variant: 'warning' },
  { key: 'Reviewed', label: 'Reviewed', variant: 'primary' },
  { key: '', label: 'New Requests', variant: 'default' },
];

const DEFAULT_STATUS_FILTER = '';

const normalizeExternalUrl = (value) => {
  if (!value) return null;

  const trimmedValue = String(value).trim();
  if (!trimmedValue) return null;

  if (/^https?:\/\//i.test(trimmedValue)) {
    return trimmedValue;
  }

  return `https://${trimmedValue}`;
};

// Only a numeric spot id can be looked up against GET /spots. Anything else in
// the spot link field (a bare note, a malformed URL) is still shown in the Spot
// Link column, but must not drive a product lookup — an unusable id would come
// back empty and get reported as a blank spot, which is a different fact.
const toQueryableSpotId = (value) => (/^\d+$/.test(String(value || '').trim()) ? String(value).trim() : '');

// Extracts the trailing spot id/number from a full spot link (or returns the value itself if it's already just an id).
const extractSpotId = (value) => {
  if (!value) return '';

  const trimmedValue = String(value).trim().replace(/\/+$/, '');
  if (!trimmedValue) return '';

  const segments = trimmedValue.split('/');
  return segments[segments.length - 1] || trimmedValue;
};

function NewProductsRequests() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);  // Start true to show spinner on initial load
  const [apiError, setApiError] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS_FILTER);
  const [selectedUser, setSelectedUser] = useState('All');
  const [selectedVarietyType, setSelectedVarietyType] = useState('All');
  const [draftById, setDraftById] = useState({});
  const [dirtyIds, setDirtyIds] = useState(() => new Set());
  const [isSavingById, setIsSavingById] = useState({});
  const [rowMessageById, setRowMessageById] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const copyResetTimer = useRef(null);

  // Spot products modal, opened from the Variety Type cell. `status` is one of
  // 'loading' | 'loaded' | 'error'; `products` is the spot's product list.
  const [spotModal, setSpotModal] = useState(null);
  // Monotonic id for in-flight spot fetches. Clicking a second row before the
  // first response lands must not let the stale response paint over the newer
  // request, so only the latest request id is allowed to commit.
  const spotRequestId = useRef(0);

  useEffect(() => () => clearTimeout(copyResetTimer.current), []);

  useEffect(() => {
    // Wait for auth to settle before firing a request without a bearer
    if (authLoading) {
      return;
    }

    // Auth done but not authenticated - stop loading, don't fetch
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    let cleanup = null;

    (async () => {
      setIsLoading(true);
      setApiError(false);

      try {
        const fetched = await fetchIssues(['pm_new_product']);

        if (fetched === null) {
          setApiError(true);
        } else {
          setRows(fetched);
          cleanup = createSubscription(
            'swissues',
            (newData) => setRows(newData),
            () => fetchIssues(['pm_new_product'])
          );
        }
      } catch (err) {
        console.error('[NewProductsRequests] API load error:', err);
        setApiError(true);
      } finally {
        setIsLoading(false);
      }
    })();

    return () => {
      if (cleanup) cleanup();
    };
  }, [authLoading, isAuthenticated]);

  useEffect(() => {
    setDraftById((prev) => {
      const next = { ...prev };
      rows.forEach((row) => {
        // Only auto-sync from server data when the user hasn't started editing this
        // row locally, otherwise unsaved edits would be stomped by incoming updates.
        // Rows without a local draft yet are always initialized from server data.
        if (!next[row.id] || !dirtyIds.has(row.id)) {
          next[row.id] = {
            status: row.resolutionStatus || '',
            notes: row.resolutionNotes || '',
          };
        }
      });
      return next;
    });
  }, [rows, dirtyIds]);


  const userOptions = useMemo(() => {
    const unique = Array.from(new Set(rows.map((row) => row.userEmail).filter(Boolean))).sort();
    return ['All', ...unique];
  }, [rows]);

  const varietyTypeOptions = useMemo(() => {
    const unique = Array.from(new Set(rows.map((row) => row.level).filter(Boolean))).sort();
    return ['All', ...unique];
  }, [rows]);

  const attributeFilteredRows = useMemo(() => {
    return rows.filter((row) => {
      const matchesUser = selectedUser === 'All' || (row.userEmail || '') === selectedUser;
      const matchesVarietyType = selectedVarietyType === 'All' || (row.level || '') === selectedVarietyType;
      return matchesUser && matchesVarietyType;
    });
  }, [rows, selectedUser, selectedVarietyType]);

  const searchFilteredRows = useMemo(() => {
    if (!searchInput.trim()) return attributeFilteredRows;

    const q = searchInput.toLowerCase();
    return attributeFilteredRows.filter((row) => {
      const created = row.createdAt ? new Date(row.createdAt).toLocaleString().toLowerCase() : '';
      return (
        (row.spotNumberLink || '').toLowerCase().includes(q) ||
        (row.suggestedDescription || '').toLowerCase().includes(q) ||
        (row.level || '').toLowerCase().includes(q) ||
        (row.referenceLink || '').toLowerCase().includes(q) ||
        (row.resolutionStatus || '').toLowerCase().includes(q) ||
        (row.resolutionNotes || '').toLowerCase().includes(q) ||
        (row.user_email || row.userEmail || '').toLowerCase().includes(q) ||
        created.includes(q)
      );
    });
  }, [attributeFilteredRows, searchInput]);

  // Per-status breakdown across the current search/user/variety-type filters,
  // independent of which status toggles are active - used for the header badges.
  const statusCounts = useMemo(() => {
    const counts = { '': 0, Pending: 0, Reviewed: 0, Created: 0 };
    searchFilteredRows.forEach((row) => {
      const key = row.resolutionStatus || '';
      if (key in counts) counts[key] += 1;
    });
    return counts;
  }, [searchFilteredRows]);

  const filteredRows = useMemo(() => {
    return searchFilteredRows.filter((row) => (row.resolutionStatus || '') === statusFilter);
  }, [searchFilteredRows, statusFilter]);

  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    });
  }, [filteredRows]);

  // Copies a suggested description so it can be pasted straight into the
  // offline product form. Mirrors ProductsManager.copyUPCToClipboard: the
  // Clipboard API needs a secure context, so fall back to a hidden textarea
  // for plain-HTTP deployments. The check mark marks which row was copied,
  // which matters when working down a long list.
  const handleCopyDescription = async (rowId, text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setCopiedId(rowId);
      // Clear any pending reset so copying a second row doesn't cut its own
      // confirmation short on the previous row's timer.
      clearTimeout(copyResetTimer.current);
      copyResetTimer.current = setTimeout(() => setCopiedId(null), 1500);
    } catch (error) {
      console.error('[NewProductsRequests] Failed to copy description:', error);
    }
  };

  // Opens the spot products modal. Reuses fetchSpotProducts — the exact same
  // call the Products Manager spot search makes for a single spot (GET
  // /spots/{id}) — so this page adds no new backend surface. The only
  // difference is what we do with the answer: list it read-only instead of
  // turning it into a catalog UPC filter.
  const handleOpenSpotProducts = async (spotId) => {
    const requestId = spotRequestId.current + 1;
    spotRequestId.current = requestId;

    setSpotModal({ spotId, status: 'loading', products: [] });

    const result = await fetchSpotProducts(spotId);

    // A newer click superseded this one — drop the response.
    if (spotRequestId.current !== requestId) return;

    if (result === null) {
      setSpotModal({ spotId, status: 'error', products: [] });
      return;
    }

    // fetchSpotProducts always answers { spots: [...] }; for a single id the
    // spot we asked for is the only entry, and its products array is what we
    // list. A spot with nothing in it yields [], which is the blank case.
    const products = result.spots?.[0]?.products || [];
    setSpotModal({ spotId, status: 'loaded', products });
  };

  const handleCloseSpotProducts = () => {
    // Bump the request id so any in-flight fetch can't reopen the modal.
    spotRequestId.current += 1;
    setSpotModal(null);
  };

  const handleDraftChange = (id, field, value) => {
    setDraftById((prev) => ({
      ...prev,
      [id]: {
        status: prev[id]?.status || '',
        notes: prev[id]?.notes || '',
        [field]: value,
      },
    }));
    setDirtyIds((prev) => new Set(prev).add(id));
  };

  const handleSaveRow = async (rowId) => {
    const draft = draftById[rowId] || { status: '', notes: '' };
    setIsSavingById((prev) => ({ ...prev, [rowId]: true }));
    setRowMessageById((prev) => ({ ...prev, [rowId]: '' }));

    const { error } = await updateIssue(rowId, {
      resolution_status: draft.status,
      resolution_notes: draft.notes,
    });

    if (error) {
      setRowMessageById((prev) => ({ ...prev, [rowId]: 'Save failed. Please retry.' }));
      setIsSavingById((prev) => ({ ...prev, [rowId]: false }));
      return;
    }

    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              resolutionStatus: draft.status,
              resolutionNotes: draft.notes,
            }
          : row
      )
    );

    setDirtyIds((prev) => {
      const next = new Set(prev);
      next.delete(rowId);
      return next;
    });
    setRowMessageById((prev) => ({ ...prev, [rowId]: 'Saved' }));
    setIsSavingById((prev) => ({ ...prev, [rowId]: false }));
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="New Products Requests"
        description="Requests created from Products Manager (source: pm_new_product)"
        actions={
          <div className="flex flex-col items-end gap-1.5">
            {/* The per-status badges are the status filter — clicking one
                selects that bucket. Total is a plain summary of everything
                matching the search / user / variety filters, i.e. the sum of
                the four buckets, so it stays non-interactive. */}
            <div
              className="flex items-center gap-1.5 flex-wrap justify-end"
              role="radiogroup"
              aria-label="Filter by status"
            >
              <Badge variant="success" className="text-sm">
                Total: {searchFilteredRows.length}
              </Badge>
              {STATUS_FILTER_OPTIONS.map(({ key, label, variant }) => (
                <button
                  key={key || 'blank'}
                  type="button"
                  role="radio"
                  aria-checked={statusFilter === key}
                  onClick={() => setStatusFilter(key)}
                  className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1"
                >
                  <Badge
                    variant={variant}
                    size="sm"
                    className={cn(
                      'cursor-pointer transition-opacity',
                      statusFilter === key
                        ? 'ring-2 ring-primary-600 ring-offset-1'
                        : 'opacity-60 hover:opacity-100'
                    )}
                  >
                    {label}: {statusCounts[key]}
                  </Badge>
                </button>
              ))}
            </div>
            <a
              href={CREATE_PRODUCTS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800"
            >
              Create Products <ExternalLink size={14} />
            </a>
          </div>
        }
      />

      {/* Search & Filters */}
      <Card className="p-4 space-y-3">
        <div className="max-w-xl">
          <Input
            placeholder="Search by spot link, description, reference, status, notes, or user"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            leadingIcon={<Search size={18} />}
          />
        </div>

        {/* Filters */}
        <div className="flex gap-4 flex-wrap items-center">
          {/* User Filter */}
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-neutral-400" />
            <Select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              options={userOptions.map((u) => ({ value: u, label: u }))}
            />
          </div>

          {/* Variety Type Filter */}
          <div className="flex items-center gap-2 border-l border-neutral-200 pl-4">
            <Select
              value={selectedVarietyType}
              onChange={(e) => setSelectedVarietyType(e.target.value)}
              options={varietyTypeOptions.map((v) => ({ value: v, label: v }))}
            />
          </div>
        </div>
      </Card>

      {/* Requests Table */}
      {isLoading ? (
        <Card className="flex items-center justify-center h-64">
          <Spinner size="lg" label="Loading new product requests..." />
        </Card>
      ) : apiError ? (
        <Card className="flex items-center justify-center h-64 bg-error-50 border-error-200">
          <p className="text-error-700 font-medium">Could not load requests from the API.</p>
        </Card>
      ) : sortedRows.length === 0 ? (
        <Card>
          <EmptyState
            icon={<PackagePlus />}
            title="No new product requests found"
            description={searchInput ? 'Try adjusting your search' : 'Requests will appear here when created from Products Manager'}
          />
        </Card>
      ) : (
        <Card noPadding className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1300px]">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">Created</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">Spot Link</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">Suggested Description</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">Variety Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">Reference Link</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">Requested By</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">Notes</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {sortedRows.map((row) => {
                  const spotNumber = String(row.spotNumberLink || '').trim();
                  const spotUrl = normalizeExternalUrl(spotNumber);
                  const spotId = extractSpotId(spotNumber);
                  const queryableSpotId = toQueryableSpotId(spotId);
                  const referenceUrl = normalizeExternalUrl(row.referenceLink);
                  const draft = draftById[row.id] || {
                    status: row.resolutionStatus || '',
                    notes: row.resolutionNotes || '',
                  };
                  const isSaving = Boolean(isSavingById[row.id]);
                  const rowMessage = rowMessageById[row.id] || '';

                  return (
                    <tr key={row.id} className="hover:bg-neutral-50 align-top">
                      <td className="px-4 py-3 text-sm text-neutral-700 whitespace-nowrap">
                        {row.createdAt ? new Date(row.createdAt).toLocaleString() : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-700 whitespace-nowrap">
                        {spotUrl ? (
                          <a
                            href={spotUrl}
                            target={SPOT_WINDOW_NAME}
                            className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-800"
                          >
                            {spotId} <ExternalLink size={14} />
                          </a>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-700 min-w-[240px]">
                        {row.suggestedDescription ? (
                          <div className="flex items-start gap-1.5">
                            <span className="flex-1">{row.suggestedDescription}</span>
                            <button
                              type="button"
                              onClick={() => handleCopyDescription(row.id, row.suggestedDescription)}
                              title="Copy description"
                              aria-label={`Copy suggested description: ${row.suggestedDescription}`}
                              className="flex-shrink-0 p-1 rounded text-neutral-400 hover:text-primary-600 hover:bg-neutral-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                            >
                              {copiedId === row.id ? (
                                <Check size={14} className="text-success-600" />
                              ) : (
                                <Copy size={14} />
                              )}
                            </button>
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-700">
                        {queryableSpotId ? (
                          <button
                            type="button"
                            onClick={() => handleOpenSpotProducts(queryableSpotId)}
                            title={`View products in spot ${queryableSpotId}`}
                            aria-label={`View products in spot ${queryableSpotId}`}
                            className="inline-flex items-center gap-1 text-left text-primary-600 underline decoration-dotted underline-offset-2 hover:text-primary-800 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                          >
                            {row.level || 'View products'}
                            <PackageSearch size={14} className="flex-shrink-0" />
                          </button>
                        ) : (
                          row.level || '-'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-700">
                        {row.referenceLink ? (
                          referenceUrl ? (
                            <a
                              href={referenceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-800"
                            >
                              <span className="truncate max-w-[150px]">{row.referenceLink}</span>
                              <ExternalLink size={14} className="flex-shrink-0" />
                            </a>
                          ) : (
                            <span className="truncate max-w-[150px] block">{row.referenceLink}</span>
                          )
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-700">{row.userEmail || '-'}</td>
                      <td className="px-4 py-3 text-sm text-neutral-700 min-w-[140px]">
                        <Select
                          value={draft.status}
                          onChange={(e) => handleDraftChange(row.id, 'status', e.target.value)}
                          disabled={isSaving}
                          options={[
                            { value: '', label: '-' },
                            ...STATUS_OPTIONS.map((status) => ({ value: status, label: status }))
                          ]}
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-700 min-w-[200px]">
                        <textarea
                          value={draft.notes}
                          onChange={(e) => handleDraftChange(row.id, 'notes', e.target.value)}
                          disabled={isSaving}
                          rows={2}
                          className="w-full px-2 py-1.5 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60"
                          placeholder="Add resolution notes"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-700 whitespace-nowrap">
                        <Button
                          size="sm"
                          onClick={() => handleSaveRow(row.id)}
                          loading={isSaving}
                          leftIcon={<Save size={14} />}
                        >
                          Save
                        </Button>
                        {rowMessage && (
                          <div className={`mt-2 text-xs ${rowMessage === 'Saved' ? 'text-success-700' : 'text-error-700'}`}>
                            {rowMessage}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Spot products — opened from the Variety Type cell */}
      <Modal
        open={Boolean(spotModal)}
        onClose={handleCloseSpotProducts}
        title={spotModal ? `Products in spot ${spotModal.spotId}` : 'Products in spot'}
        size="4xl"
      >
        {spotModal?.status === 'loading' && (
          <div className="flex items-center justify-center py-10">
            <Spinner size="lg" label="Loading spot products..." />
          </div>
        )}

        {spotModal?.status === 'error' && (
          <div className="py-8 text-center">
            <p className="text-error-700 font-medium">Could not load products for this spot.</p>
            <p className="mt-1 text-sm text-neutral-500">
              Spot {spotModal.spotId} may not exist, or the request failed. Try again.
            </p>
          </div>
        )}

        {spotModal?.status === 'loaded' && (
          spotModal.products.length === 0 ? (
            // Covers both a spot that exists with nothing entered on it and a
            // spot id that matched no row — to whoever is working this list
            // they are the same fact: there is nothing on the spot to review.
            <div className="py-10 text-center">
              <p className="text-lg font-semibold text-neutral-900">This spot is blank</p>
              <p className="mt-1 text-sm text-neutral-500">
                No products are currently entered on spot {spotModal.spotId}.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <Badge variant="default" size="sm">
                {spotModal.products.length} product{spotModal.products.length === 1 ? '' : 's'}
              </Badge>

              <div className="max-h-[60vh] overflow-auto border border-neutral-200 rounded-md">
                <table className="w-full min-w-[640px]">
                  <thead className="bg-neutral-50 border-b border-neutral-200 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-600">UPC</th>
                      <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-600">Brand</th>
                      <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-600">Description</th>
                      <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-600">Category</th>
                      <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-600">Size</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {spotModal.products.map((product, index) => (
                      <tr key={`${product.upc || 'no-upc'}-${index}`} className="hover:bg-neutral-50">
                        <td className="px-3 py-2 text-sm font-mono text-neutral-700 whitespace-nowrap">{product.upc || '-'}</td>
                        <td className="px-3 py-2 text-sm text-neutral-700">{product.brand || '-'}</td>
                        <td className="px-3 py-2 text-sm text-neutral-700">{product.description || '-'}</td>
                        <td className="px-3 py-2 text-sm text-neutral-700">{product.category || '-'}</td>
                        <td className="px-3 py-2 text-sm text-neutral-700 whitespace-nowrap">{product.size || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        )}

      </Modal>
    </div>
  );
}

export default NewProductsRequests;
